import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { paths } from "../../paths";

import { SimplifiedView } from "../../svgs/simplified-view";
import { Trade } from "../../svgs/trade";
import { Custody } from "../../svgs/custody";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";

import { Help } from "../../svgs/help";
import { Account } from "../../svgs/account";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { getEnv } from "@ace/env";
import {
  ARCHAX_ACADEMY_LINK,
  ONBOARDING_USER_ROLES,
  TRADE_DASHBOARD_ACCESS_ROLES,
} from "../../constants";
import { Theme, useMediaQuery } from "@mui/material";
import {
  hasOneOfRolesInAnyOrg,
  hasTheseRolesOnlyInAnyOrg,
} from "@archax/web-lib";
import { featureFlags } from "../../feature-flags";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

interface UseSectionProps {
  token: string;
}

export const useSections = (props: UseSectionProps) => {
  const { t } = useTranslation();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  return useMemo(() => {
    if (props.token === "") return [];
    return [
      {
        items: [
          hasOneOfRolesInAnyOrg(TRADE_DASHBOARD_ACCESS_ROLES, props.token) ||
          hasTheseRolesOnlyInAnyOrg(ONBOARDING_USER_ROLES, props.token)
            ? {
                title: "Buy & Sell",
                path: paths.simplifiedView,
                icon: <SimplifiedView />,
              }
            : false,
          mdUp || featureFlags.advancedMobileView
            ? {
                title: "Advanced Trading",
                path: paths.tradeDashboard,
                icon: <Trade />,
              }
            : false,
          {
            title: "Custody",
            icon: <Custody />,
            path: paths.custody,
            items: [
              {
                title: "Transactions",
                label: "transactions-drawer",
                path: paths.transactions,
              },
              {
                title: "Funding Status",
                label: "funding-status-drawer",
                path: paths.fundingStatus,
              },
            ],
          },
        ].filter((s) => s !== false),
      },
    ] as Section[];
  }, [t, mdUp, props.token]);
};

export const footerSection: Section[] = [
  {
    items: [
      {
        title: "Account",
        icon: <Account />,
        label: "overview-nav",
        path: paths.accountOverview,
      },
      {
        title: "Document Library",
        icon: <AutoStoriesOutlinedIcon />,
        label: "document-library-nav",
        path: paths.documentLibrary,
      },
      {
        title: "Academy",
        icon: <SchoolOutlinedIcon />,
        label: "academy-nav",
        external: true,
        path: ARCHAX_ACADEMY_LINK,
      },
      {
        title: "Help",
        path: `mailto:${getEnv("ARCHAX_EMAIL_SUPPORT")}`,
        external: true,
        icon: <Help />,
      },
    ],
  },
];

export const useThemeConfig = (theme: Theme) => {
  const evidentDefaultTheme = {
    "--nav-bg": theme.palette.neutral[800],
    "--nav-color": theme.palette.common.white,
    "--nav-border-color": "transparent",
    "--nav-logo-border": theme.palette.neutral[700],
    "--nav-section-title-color": theme.palette.neutral[400],
    "--nav-item-color": theme.palette.neutral[400],
    "--nav-item-hover-bg": theme.palette.primary.main,
    "--nav-item-active-bg": theme.palette.primary.main,
    "--nav-item-active-color": theme.palette.common.white,
    "--nav-item-disabled-color": theme.palette.neutral[500],
    "--nav-item-icon-color": theme.palette.neutral[400],
    "--nav-item-icon-active-color": theme.palette.common.white,
    "--nav-item-icon-disabled-color": theme.palette.neutral[500],
    "--nav-item-chevron-color": theme.palette.neutral[600],
    "--nav-scrollbar-color": theme.palette.neutral[400],
    "--nav-collapse-hover": theme.palette.primary.main,
  };

  const blendInDefaultTheme = {
    "--nav-bg": theme.palette.background.default,
    "--nav-color": theme.palette.neutral[100],
    "--nav-border-color": theme.palette.neutral[700],
    "--nav-logo-border": theme.palette.neutral[700],
    "--nav-section-title-color": theme.palette.neutral[400],
    "--nav-item-color": theme.palette.neutral[400],
    "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
    "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
    "--nav-item-active-color": theme.palette.text.primary,
    "--nav-item-disabled-color": theme.palette.neutral[600],
    "--nav-item-icon-color": theme.palette.neutral[500],
    "--nav-item-icon-active-color": theme.palette.primary.main,
    "--nav-item-icon-disabled-color": theme.palette.neutral[700],
    "--nav-item-chevron-color": theme.palette.neutral[700],
    "--nav-scrollbar-color": theme.palette.neutral[400],
    "--nav-collapse-hover": theme.palette.neutral[800],
  };

  const discreteDefaultTheme = {
    "--nav-bg": theme.palette.neutral[900],
    "--nav-color": theme.palette.neutral[100],
    "--nav-border-color": theme.palette.neutral[700],
    "--nav-logo-border": theme.palette.neutral[700],
    "--nav-section-title-color": theme.palette.neutral[400],
    "--nav-item-color": theme.palette.text.primary,
    "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
    "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
    "--nav-item-active-color": theme.palette.text.primary,
    "--nav-item-disabled-color": theme.palette.neutral[600],
    "--nav-item-icon-color": theme.palette.neutral[500],
    "--nav-item-icon-active-color": theme.palette.primary.main,
    "--nav-item-icon-disabled-color": theme.palette.neutral[700],
    "--nav-item-chevron-color": theme.palette.neutral[500],
    "--nav-scrollbar-color": theme.palette.neutral[400],
    "--nav-collapse-hover": theme.palette.neutral[800],
  };

  return {
    "blend-in": {
      dark: blendInDefaultTheme,
      light: {
        ...blendInDefaultTheme,
        "--nav-color": theme.palette.text.primary,
        "--nav-border-color": theme.palette.neutral[100],
        "--nav-logo-border": theme.palette.neutral[100],
        "--nav-item-color": theme.palette.text.secondary,
        "--nav-item-hover-bg": theme.palette.action.hover,
        "--nav-item-active-bg": theme.palette.action.selected,
        "--nav-item-disabled-color": theme.palette.neutral[400],
        "--nav-item-icon-color": theme.palette.neutral[400],
        "--nav-item-icon-disabled-color": theme.palette.neutral[400],
        "--nav-item-chevron-color": theme.palette.neutral[400],
        "--nav-scrollbar-color": theme.palette.neutral[900],
        "--nav-collapse-hover": theme.palette.neutral[200],
      },
    },
    discrete: {
      dark: discreteDefaultTheme,
      light: {
        ...discreteDefaultTheme,
        "--nav-bg": theme.palette.neutral[50],
        "--nav-color": theme.palette.text.primary,
        "--nav-border-color": theme.palette.divider,
        "--nav-logo-border": theme.palette.neutral[200],
        "--nav-section-title-color": theme.palette.neutral[500],
        "--nav-item-color": theme.palette.neutral[500],
        "--nav-item-hover-bg": theme.palette.action.hover,
        "--nav-item-active-bg": theme.palette.action.selected,
        "--nav-item-disabled-color": theme.palette.neutral[400],
        "--nav-item-icon-color": theme.palette.neutral[600],
        "--nav-item-icon-disabled-color": theme.palette.neutral[400],
        "--nav-item-chevron-color": theme.palette.neutral[400],
        "--nav-scrollbar-color": theme.palette.neutral[900],
        "--nav-collapse-hover": theme.palette.neutral[100],
      },
    },
    evident: {
      dark: evidentDefaultTheme,
      light: evidentDefaultTheme,
    },
  };
};
